import type { UiDefinition } from './helios/templateForms';
import type { FormItemUiDefinition, RecordsViewParam } from './formItem.ts';
import type { PrintConfiguration } from './templates';

export interface DisplayColumn {
  itemId: number;
  order: number;
}

export type HeliosItemType = 'ColumnValue' | 'AggregationGroup' | 'UserDefined' | 'ButtonWithAction';
export type HeliosUserDefinitionAttributeType = 'Date' | 'Time';

export enum TemplatePresentationItemType {
  ColumnValue = 'ColumnValue',
  AggregationGroup = 'AggregationGroup',
  UserDefined = 'UserDefined',
}

enum TemplateDataType {
  Int32 = 'Int32',
  Int64 = 'Int64',
  Decimal = 'Decimal',
  String = 'String',
  DateTime = 'DateTime',
  Boolean = 'Boolean',
  GuidArray = 'GuidArray',
  LinkToAgenda = 'LinkToAgenda',
}

export enum HeliosRecordSystemDatasource {
  HeliosUser = 'HeliosUser',
  HeliosUserId = 'HeliosUserId',
  UserReference = 'UserReference',
  UserRole = 'UserRole',
  UserEmail = 'UserEmail',
  DateTimeNow = 'DateTimeNow',
  DateNow = 'DateNow',
}

export enum LogicalOperator {
  None = 'None',
  And = 'And',
  Or = 'Or',
}

export enum ComparisonOperator {
  GreaterThan = 'GreaterThan',
  GreaterThanOrEqual = 'GreaterThanOrEqual',
  LessThan = 'LessThan',
  LessThanOrEqual = 'LessThanOrEqual',
  Empty = 'Empty',
  NotEmpty = 'NotEmpty',
  Null = 'Null',
  NotNull = 'NotNull',
  Equals = 'Equals',
  NotEquals = 'NotEquals',
  Contains = 'Contains',
  NotContains = 'NotContains',
  Between = 'Between',
}

export enum TemplatePresentationItemDisplay {
  Value = 'Value',
  StaticSelection = 'StaticSelection',
  Browse = 'Browse',
  File = 'File',
  Link = 'Link',
  Button = 'Button',
  CalendarName = 'CalendarName',
  CalendarCategory = 'CalendarCategory',
  CalendarDateFrom = 'CalendarDateFrom',
  CalendarDateTo = 'CalendarDateTo',
  CalendarAllDay = 'CalendarAllDay',
  InfoText = 'InfoText',
}

enum TextAlignValue {
  Center = 'Center',
  Left = 'Left',
  Right = 'Right',
}

enum ThousandsSeparatorValue {
  Space = 'Space',
  Dot = 'Dot',
  Comma = 'Comma',
}

enum MathRoundValue {
  Arithmetic = 'Arithmetic',
  Up = 'Up',
  Down = 'Down',
}

export enum RoundedToValue {
  TenThousandths = 'TenThousandths',
  Thousandths = 'Thousandths',
  Hundredths = 'Hundredths',
  Tenths = 'Tenths',
  Dozens = 'Dozens',
  Hundreds = 'Hundreds',
  Thousands = 'Thousands',
}

export enum ConditionValueSource {
  None = 'None',
  CurrentTemplate = 'CurrentTemplate',
  LinkedTemplate = 'LinkedTemplate',
}

export enum DataTableType {
  Table = 'Table',
  Detail = 'Detail',
  TableDetail = 'TableDetail',
}

export enum DataTableFormatType {
  Excel = 'Excel',
  Pdf = 'Pdf',
}

export type HeliosTranslations = {
  language: string;
  value: string;
};

type HeliosRecordColumn = {
  itemId: number;
  itemType: TemplatePresentationItemType;
  columnKey: string;
  columnName: string;
  dataType: TemplateDataType;
  rawValue: string;
  formattedValue: string;
  dynamicRelationValuesCount: number;
  thousandsSeparator: string;
  unit: string;
};

export type HeliosRecordFunction = {
  id: string;
  functionId: string;
  isCommon: null | boolean;
  functionType: string;
  workflowCommentType: string;
  alias: string;
  iconName: string;
  color: string;
  overrideLeftGesture: boolean;
  overrideRightGesture: boolean;
  showAsStandaloneButton?: boolean;
  requiresConfirmation: boolean;
  allowMultipleSelection: boolean;
  mobileAlias: string;
  mobileColor: string;
  mobileIconName: string;
  tooltip: string;
  order: number;
  openCreatedRecord?: boolean;
  openCreatedRecordTemplateId?: string | null;
  aliasTranslations?: HeliosTranslations[];
  mobileAliasTranslations?: HeliosTranslations[];
  tooltipTranslations?: HeliosTranslations[];
};

type HeliosRecordItem = {
  itemId: number;
  itemType: TemplatePresentationItemType;
  itemDisplayType: TemplatePresentationItemDisplay;
  columnKey: string;
  columnName: string;
  dataType: TemplateDataType;
  rawValue: string;
  formattedValue: string;
  dynamicRelationValuesCount: number;
  columnType: HeliosColumnTypes | null;
  thousandsSeparator: string | null;
  unit: string | null;
  userDefinedAttributeType: HeliosUserDefinitionAttributeType | null;
  uiDefinition: FormItemUiDefinition | null;
  relationDynamicValue: RelationshipDynamicValueType | null;
  relationStaticValue: RelationshipStaticValueType | null;
};

export type RelationshipDynamicValueType = { count: number } | null;
export type RelationshipStaticValueType = { id: number; name: string; reference: string } | null;
export type HeliosColumnTypes = 'Attribute' | 'RelationStatic' | 'RelationDynamic';

export type DataRecordColumn = {
  columnKey: string;
  columnName: string;
  itemId: number;
  itemType: TemplatePresentationItemType;
  dataType: TemplateDataType;
  rawValue: { id: string; name: string } | string;
  columnType?: HeliosColumnTypes;
  relationDynamicValue?: RelationshipDynamicValueType;
  relationStaticValue?: RelationshipStaticValueType;
  formattedValue: string;
  dynamicRelationValuesCount: number;
  thousandsSeparator: string;
  unit: string;
  userDefinedAttributeType: HeliosUserDefinitionAttributeType | null;
};

export type StaticSelectionSettings = {
  selectItems: {
    key: string;
    value: string;
  }[];
};

export type HeliosRecordSectionItem = {
  dataRecordColumn: DataRecordColumn;
  columnKey: string;
  itemId: number;
  columnName: string;
  x: string;
  y: string;
  label: string;
  displayType: TemplatePresentationItemDisplay;
  isVisible: boolean;
  isEditable: boolean;
  isRequired: boolean;
  isMultiSelect?: boolean;
  isPresaveAllowed: boolean;
  buttonLabel?: string;
  color: string;
  uiDefinition: UiDefinition | null;
  settings: {
    browse: {
      codeListTemplateId: string;
      detailTemplateId: string;
      dynamicRelationId: number;
      displayColumns?: DisplayColumn[];
      allowMultipleSelection?: boolean;
      valueItemId?: number;
      flowTemplateId?: string;
      flowViewId?: string;
      flowTableId?: string;
      formItemIdToShow?: string;
      flowFunctions?: string[];
      params?: RecordsViewParam[];
    };
    staticSelection: StaticSelectionSettings;
    externalFile: {
      dynamicRelationId: number;
      folderId: number;
      viewMode?: string;
    };
    link: {
      detailTemplateId: string;
    };
    delimiter: {
      orientation: 'Horizontal' | 'Vertical';
      style: 'Solid' | 'Dotted' | 'Dashed';
    } | null;
    infoText: {
      content: HeliosTranslations[] | null;
    } | null;
  };
  formatting: {
    formatValue: {
      textAlign: TextAlignValue;
      typeSpecific: {
        formatText: {
          showAsTextArea: boolean;
          showPhoneOrMail: boolean;
          showWebLink: boolean;
          specialChars: boolean;
          showAsRichText: boolean;
        };
        formatNumber: {
          showAsTime: boolean;
          unit: string;
          thousandsSeparator: ThousandsSeparatorValue;
          mathRound: MathRoundValue;
          roundTo: RoundedToValue;
        };
        formatDateTime: {
          variant: 'Date' | 'DateTime' | 'Time' | 'MonthYear' | null;
        };
      };
    };
    formatLabel: {
      textAlign: TextAlignValue;
    };
    labelLayout?: 'SideBySide' | 'Stacked';
  };
  translations: HeliosTranslations[];
  itemDatasource: {
    typeItemDatasource: string | null;
    userDefinedAttributeId: string | null;
    templateFunctionId: string | null;
    templateDataItemId: string | null;
    agregationGroupId: string | null;
  };
  subTabId?: string | null;
};

type HeliosRecordSubTab = {
  id: string;
  order: number;
  name?: string | null;
  translations?: HeliosTranslations[] | null;
};

type HeliosRecordSection = {
  name: string;
  title: string;
  description: string;
  collapsed: boolean;
  order: number;
  items: HeliosRecordSectionItem[];
  sizeX: number;
  sizeY: number;
  translations: HeliosTranslations[];
  tabId?: string | null;
  subTabs?: HeliosRecordSubTab[] | null;
};

export type HeliosRecordTab = {
  id: string;
  order: number;
  title: string | null;
  titleTranslations: HeliosTranslations[] | null;
};

export type HeliosRecordDataTableColumnSettings = {
  browse: {
    codeListTemplateId: string;
    detailTemplateId: string;
    dynamicRelationId: number;
    displayColumns?: DisplayColumn[];
    allowMultipleSelection?: boolean;
    valueItemId?: number;
  };
  staticSelection: StaticSelectionSettings;
  externalFile: {
    dynamicRelationId: number;
    folderId: number;
    viewMode?: string;
  };
  link: {
    detailTemplateId: string;
  };
};

export type HeliosRecordDataTableColumnFormatting = {
  formatValue: {
    textAlign: TextAlignValue;
    typeSpecific: {
      formatText: {
        showAsTextArea: boolean;
        showPhoneOrMail: boolean;
        showWebLink: boolean;
        specialChars: boolean;
        showAsRichText: boolean;
      };
      formatNumber: {
        showAsTime: boolean;
        unit: string;
        thousandsSeparator: ThousandsSeparatorValue;
        mathRound: MathRoundValue;
        roundTo: RoundedToValue;
      };
      formatDateTime: {
        variant: 'Date' | 'DateTime' | 'Time' | 'MonthYear' | null;
      };
    };
  };
  formatLabel: {
    textAlign: TextAlignValue;
  };
};

export type HeliosRecordDataTablesColumn = {
  columnKey: string;
  columnName: string;
  itemId: number;
  label: string;
  displayType: TemplatePresentationItemDisplay;
  dataType: TemplateDataType;
  isEditable: boolean;
  isRequired: boolean;
  color: string;
  order: number;
  settings: HeliosRecordDataTableColumnSettings;
  formatting: HeliosRecordDataTableColumnFormatting;
  userDefinedAttributeType?: HeliosUserDefinitionAttributeType | null;
  showButtonDirectly?: boolean;
};

export type HeliosRecordActions = {
  copy: boolean;
  edit: boolean;
  copyInRow: boolean;
  delete: boolean;
  editInRow: boolean;
};

export type HeliosRecordDataTablesData = {
  recordNumber: number;
  masterRecordNumber?: number;
  recordName: string;
  recordReference: string;
  functions: HeliosRecordFunction[];
  items: HeliosRecordItem[];
  actions: HeliosRecordActions;
};

export type DynamicRelationRecord = {
  recordNumber: number;
};

export type DeleteHeliosRecordReq = {
  templateId: string;
  recordNumber: number;
  masterRecordNumber?: number;
};

export type UpdateHeliosTableRecordsReq = {
  templateId: string;
  recordNumber: number;
  columns: {
    itemId: number;
    rawValue?: string | number | null;
    addedDynamicRelationRecords?: DynamicRelationRecord[];
    removedDynamicRelationRecords?: DynamicRelationRecord[];
  }[];
};

export type UpdateHeliosRecordReq = {
  templateId: string;
  recordNumber?: number;
  masterRecordNumber?: number;
  sections?: {
    items: {
      dataRecordColumn: {
        itemId: number;
        rawValue: string;
      };
    }[];
  }[];
};

export type CreateHeliosRecordReq = {
  templateId: string;
  columns: HeliosRecordColumn[];
};

export type CreateHeliosRecordRes = {
  recordNumber: number;
  functions: HeliosRecordFunction[];
  items: HeliosRecordItem[];
  sections: HeliosRecordSection[];
  actions: HeliosRecordActions;
  count: number;
};

export type HeliosRecordDataTablesReq = {
  templateId: string;
  searchKeyword: string;
  offset: number;
  limit: number;
  orderBy: string;
  orderDesc: boolean;
  useServiceAccount: boolean;
  prioritizeRecordIds?: number[];
  externalCall?: boolean;
  recordIds?: number[];
  masterTemplate?: {
    templateId?: string;
    recordId?: string;
  };
  formData?: {
    dataItems?:
      | {
          itemId: string;
          value: string;
        }[]
      | null;
    templateId?: string | null;
    itemId?: number | null;
    recordId?: number | null;
  } | null;
  filters: {
    leftBracket: boolean;
    rightBracket: boolean;
    isBlank?: boolean | null;
    datasource: {
      templateDataItemId: number;
      systemDatasource: HeliosRecordSystemDatasource;
    };
    leftLogicalOperator: LogicalOperator;
    comparisonOperator: ComparisonOperator;
    value: {
      conditionValueSource: ConditionValueSource;
      staticValue: string;
      staticValues?: string[];
      fromValue?: string;
      toValue?: string;
      systemDatasource: HeliosRecordSystemDatasource;
      templateDataItemId: number;
    };
  }[];
};

export type HeliosRecordDataTablesRes = {
  offset: number;
  limit: number;
  count: number;
  detailTemplateId: string;
  moduleId?: string;
  data: HeliosRecordDataTablesData[];
  columns: HeliosRecordDataTablesColumn[];
  functions: HeliosRecordFunction[];
  printConfigurations: PrintConfiguration[];
  tableActions: {
    create: boolean;
    createInRow: boolean;
    export: boolean;
    delete: boolean;
  };
};

export type HeliosDataTableItems = {
  count: number;
  data: HeliosRecordDataTablesData[];
  columns: HeliosRecordDataTablesColumn[];
};

export type GetSingleHeliosRecord = {
  recordNumber: number;
  count: number;
  datasourceId: string;
  functions: HeliosRecordFunction[];
  items: HeliosRecordItem[];
  sections: HeliosRecordSection[];
  tabs?: HeliosRecordTab[];
  actions: {
    copy: true;
    edit: true;
    export: boolean;
    delete: boolean;
  };
};

export interface HeliosCalendarAppointment {
  primaryKey: number;
  subject?: string | null;
  category: string;
  colorHex: string;
  dateFrom: string;
  dateTo?: string | null;
  isAllDay?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
}

export interface HeliosColorCategory {
  category: string;
  hexColor: string;
}

export type GetHeliosCalendarsAppointments = {
  appointments: HeliosCalendarAppointment[];
  colorCategories: HeliosColorCategory[];
  availableCategories?: HeliosColorCategory[];
  detailTemplateId?: string;
  canView?: boolean;
  canCreate?: boolean;
};

export type GetHeliosCalendarsAppointmentsReq = {
  TemplateId: string;
  DateFrom: string;
  DateTo: string;
  ColorCategories?: string[];
  CategoryFilter?: string[];
};

export type ExportDataFromTableToFile = {
  overviewTemplateId?: string;
  detailTemplateId?: string;
  entryTemplateId?: string;
  exportDataTableType?: DataTableType;
  exportDataTableFormatType?: DataTableFormatType;
  filters?:
    | [
        {
          leftBracket: boolean;
          rightBracket: boolean;
          datasource: {
            templateDataItemId: number;
            systemDatasource: HeliosRecordSystemDatasource;
          };
          leftLogicalOperator: LogicalOperator;
          comparisonOperator: ComparisonOperator;
          value: {
            conditionValueSource: ConditionValueSource;
            staticValue: string;
            systemDatasource: HeliosRecordSystemDatasource;
            templateDataItemId: number;
          };
        },
      ]
    | [];
  searchKeyword?: string;
  offset?: number;
  pagesToExport?: number;
  recordsPerPage?: number;
  exportAllPages?: boolean;
  name?: string;
  recordNumber?: number;
};

export type GetHeliosDataRecordReq = {
  templateId: string;
  recordNumber: string;
  overviewTemplateId?: string;
  joinColumnFilters?: Record<string, string>;
};

export type GetHeliosDataRecordPrefilledReq = {
  templateId: string;
  recordNumber?: string;
  parentTemplateId?: string;
  parentRecordNumber?: string;
};

export type GetHeliosBrowseItemsRes = {
  recordNumber: number;
  name: string | null;
  reference: string | null;
}[];

export type GetHeliosBrowseItemsReq = {
  templateId: string;
  templateItemId: string;
  recordId?: string;
  entryId?: string;
};

export type CreateHeliosDataTableRecordRes = {
  recordNumber?: number;
  masterRecordNumber?: number;
  functions?: HeliosRecordFunction[];
  sections?: HeliosRecordSection[];
  actions?: HeliosRecordActions;
  count?: number;
  resultState?: string;
  warningMessages?: string[] | null;
  errorMessages?: string[] | null;
  record?: GetSingleHeliosRecord;
  automaticValues?: Record<number, unknown>;
};

export type CreateHeliosDataTableRecordReq = {
  templateId: string;
  masterRecordNumber?: string;
  recordNumber?: number;
  columns: {
    itemId: number;
    rawValue?: string | number | null;
    addedDynamicRelationRecords?: DynamicRelationRecord[];
    removedDynamicRelationRecords?: DynamicRelationRecord[];
  }[];
};

export type HeliosRecordDataSection = {
  id: number;
  templateId: string;
  name: string;
  description: string;
  collapsed: boolean;
  gridSizeX: number;
  gridSizeY: number;
  entry?: HeliosRecordDataTablesRes;
  datasourceId: string;
};

export type NeighborRecordNumbersRes = {
  previous: number | null;
  current: number;
  next: number | null;
  position: number;
  totalCount: number;
};

export type GetNeighborRecordNumbersReq = {
  templateId: string;
  currentRecordNumber: number;
};
